/**
 * Lolli Lifting — product gallery (design 5a).
 *
 * Own implementation, no dependency on Dawn's media-gallery.js. Four
 * jobs:
 *  1. Thumbnail click (or keyboard activation — Enter/Space on a
 *     focused thumbnail fires the same 'click') -> make that slide
 *     active (desktop: shows it, hides the rest, via CSS on
 *     .is-active; mobile: scrolls the swipe row to it). On desktop,
 *     if the selected thumbnail was the first or last one currently
 *     visible in the scrollable column, also nudges the column one
 *     step further that way (job 4's stepSize()/canScrollUp()/
 *     canScrollDown()), so the next thumbnail beyond it comes into
 *     view — a no-op for a thumbnail in the middle, or at either end
 *     of the whole list.
 *  2. Mobile swipe -> IntersectionObserver keeps thumbnails/dots in
 *     sync with whichever slide the user scrolled to.
 *  3. Desktop thumbnail column height: matches the scrollable area's
 *     cap to the main image's own rendered height (ResizeObserver on
 *     it, or a resize listener as a fallback) — not expressible in
 *     CSS alone, since the main image's height is itself fluid with
 *     the viewport. Individual thumbnail size is plain CSS
 *     (aspect-ratio, assets/section-lolli-product.css), not computed
 *     here. See snippets/lolli-gallery.liquid for the full picture.
 *  4. Desktop thumbnail column scroll (products with more than four
 *     images): up/down arrow buttons, in normal flow above/below the
 *     thumbnail strip, step the column by one thumbnail. Both
 *     buttons are always present at full size — toggling that used
 *     to shift the thumbnail stack itself whenever the up arrow
 *     appeared mid-scroll. Instead each one's active/inactive state
 *     (chevron visibility + interactivity, not its layout box) is
 *     set purely from measured scroll state (scrollHeight/
 *     clientHeight/scrollTop), never a hardcoded image count, so a
 *     product with four or fewer images shows two
 *     permanently-inactive slots rather than no arrows — there's
 *     nothing to scroll to, the measurement says so directly.
 *
 * Without JS: the mobile row is still a native scroll-snap swipe
 * gallery, and the desktop thumbnail column is still scrollable by
 * wheel/trackpad/keyboard (both are plain CSS overflow, not this
 * file) at a fixed fallback size (not matched to the main image) —
 * only the active-state syncing, the height match and the arrow
 * buttons themselves don't appear, a cosmetic gap, not a broken
 * gallery.
 */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-lolli-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('[data-lolli-gallery-main]');
    const slides = Array.from(gallery.querySelectorAll('[data-lolli-gallery-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-lolli-gallery-thumb]'));
    const dots = Array.from(gallery.querySelectorAll('.lolli-gallery__dot'));

    const thumbsColumn = gallery.querySelector('[data-lolli-gallery-thumbs]');
    const upNav = gallery.querySelector('[data-lolli-gallery-thumb-nav="up"]');
    const downNav = gallery.querySelector('[data-lolli-gallery-thumb-nav="down"]');

    const EPSILON = 1; // sub-pixel rounding slack at either end of the scroll range

    // Shared between the arrow buttons below and the thumbnail
    // click handler further down (auto-scroll on selecting the
    // first/last visible thumbnail), so both agree on what "can
    // scroll further" and "one step" mean. Safe to call even when
    // there's no thumbsColumn (single-image product, no gallery
    // thumbnails at all) — that just means these always report
    // false / a no-op step, and nothing below ever calls them in
    // that case anyway since the thumbs array is empty too.
    const canScrollUp = () => !!thumbsColumn && thumbsColumn.scrollTop > EPSILON;
    const canScrollDown = () =>
      !!thumbsColumn &&
      thumbsColumn.scrollTop + thumbsColumn.clientHeight < thumbsColumn.scrollHeight - EPSILON;

    // One thumbnail's height plus the gap after it — read from the
    // rendered DOM/computed styles, not the custom properties'
    // literal values, so this still steps correctly even if a
    // future override changes them outside this file.
    const stepSize = () => {
      if (!thumbsColumn) return 0;
      const firstThumb = thumbsColumn.querySelector('[data-lolli-gallery-thumb]');
      const gap = parseFloat(getComputedStyle(thumbsColumn).rowGap) || 0;
      return (firstThumb ? firstThumb.getBoundingClientRect().height : thumbsColumn.clientHeight) + gap;
    };

    if (thumbsColumn && upNav && downNav && main) {
      // Both buttons keep their box at all times (see
      // section-lolli-product.css's --inactive rules) — only the
      // chevron's visibility and the button's own interactivity
      // toggle, so an arrow appearing/disappearing never changes the
      // thumbnail stack's position.
      const setNavActive = (button, active) => {
        button.classList.toggle('lolli-gallery__thumb-nav--inactive', !active);
        button.tabIndex = active ? 0 : -1;
      };

      const updateThumbNav = () => {
        setNavActive(upNav, canScrollUp());
        setNavActive(downNav, canScrollDown());
      };

      // Match the scrollable thumbnail area's cap to the main image's
      // own rendered height — a product with enough images to exceed
      // that scrolls, anything shorter just ends naturally (max-height
      // on .lolli-gallery__thumbs, not a fixed height). Individual
      // thumbnail size is NOT computed here any more: it used to be
      // mainHeight divided by the thumbnail count (capped at 4), which
      // stretched thumbnails on a product with only two or three
      // images well past their natural proportions — badly cropped by
      // the img's own object-fit: cover. assets/section-lolli-
      // product.css now sizes each thumbnail with a fixed
      // aspect-ratio instead (matching the main image's own 1:1), so
      // thumbnails are never stretched to fill available space; this
      // only needs to hand over the one value CSS genuinely can't
      // derive on its own (the main image's fluid rendered height).
      const syncThumbsHeight = () => {
        const mainHeight = main.getBoundingClientRect().height;
        if (!mainHeight) return;
        thumbsColumn.style.setProperty('--lolli-thumb-max-height', `${mainHeight}px`);
        updateThumbNav();
      };

      upNav.addEventListener('click', () => {
        thumbsColumn.scrollBy({ top: -stepSize(), behavior: 'smooth' });
      });
      downNav.addEventListener('click', () => {
        thumbsColumn.scrollBy({ top: stepSize(), behavior: 'smooth' });
      });

      thumbsColumn.addEventListener('scroll', updateThumbNav);

      if ('ResizeObserver' in window) {
        new ResizeObserver(syncThumbsHeight).observe(main);
      } else {
        window.addEventListener('resize', syncThumbsHeight);
      }
      syncThumbsHeight();
      updateThumbNav();
    }

    if (slides.length < 2 || !main) return;

    const setActive = (index) => {
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        setActive(i);
        // Only scroll if .lolli-gallery__main is actually a scroll
        // container (mobile row) — on desktop the inactive slides are
        // display:none, so there's nothing to scroll to and this
        // would otherwise risk an unwanted page-level scroll.
        if (main.scrollWidth > main.clientWidth) {
          slides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }

        // Desktop thumbnail column: selecting the first or last
        // currently-visible thumbnail nudges the column one step
        // further in that direction, so the next thumbnail beyond it
        // comes into view — a hint that there's more above/below,
        // not just a way to reach it via the arrows. A thumbnail
        // that isn't at either visible edge doesn't move anything,
        // and canScrollUp()/canScrollDown() keep this a no-op at the
        // actual ends of the list (clicking the first thumbnail while
        // already scrolled to the top does nothing, same as the
        // arrow buttons would). Native button activation (Enter/
        // Space on a focused thumbnail) fires this same 'click'
        // event, so keyboard selection gets identical behaviour for
        // free — there's no separate arrow-key-driven thumbnail
        // stepping in this file to mirror it in.
        if (thumbsColumn) {
          const columnRect = thumbsColumn.getBoundingClientRect();
          const thumbRect = thumb.getBoundingClientRect();
          if (Math.abs(thumbRect.top - columnRect.top) <= EPSILON && canScrollUp()) {
            thumbsColumn.scrollBy({ top: -stepSize(), behavior: 'smooth' });
          } else if (Math.abs(thumbRect.bottom - columnRect.bottom) <= EPSILON && canScrollDown()) {
            thumbsColumn.scrollBy({ top: stepSize(), behavior: 'smooth' });
          }
        }
      });
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              const index = slides.indexOf(entry.target);
              if (index > -1) setActive(index);
            }
          });
        },
        { root: main, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }
  });
});
